'use client';
import React, { useState, useRef } from 'react';

const FORMATS = [
  { value: 'image/jpeg', label: 'JPG', ext: 'jpg' },
  { value: 'image/png', label: 'PNG', ext: 'png' },
  { value: 'image/webp', label: 'WebP', ext: 'webp' },
];

function formatBytes(bytes: number): string {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

export default function FormatConverterTool() {
  const [image, setImage] = useState<{ file: File; url: string; size: number } | null>(null);
  const [outputFormat, setOutputFormat] = useState('image/png');
  const [result, setResult] = useState<{ url: string; size: number; ext: string } | null>(null);
  const [processing, setProcessing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadImage = (file: File) => {
    if (!file.type.match(/^image\/(jpeg|png|webp)$/)) { alert('Please upload a JPG, PNG, or WebP image.'); return; }
    setImage({ file, url: URL.createObjectURL(file), size: file.size });
    setResult(null);
  };

  const handleConvert = async () => {
    if (!image) return;
    setProcessing(true);
    try {
      const img = new window.Image();
      img.src = image.url;
      await new Promise<void>((resolve) => { img.onload = () => resolve(); });
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Canvas not supported');
      if (outputFormat === 'image/jpeg') {
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
      ctx.drawImage(img, 0, 0);
      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob((b) => { if (b) resolve(b); else reject(new Error('Conversion failed')); }, outputFormat, 0.92);
      });
      const fmt = FORMATS.find((f) => f.value === outputFormat)!;
      setResult({ url: URL.createObjectURL(blob), size: blob.size, ext: fmt.ext });
    } catch (err) {
      console.error(err);
      alert('Conversion failed. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const handleDownload = () => {
    if (!result || !image) return;
    const a = document.createElement('a');
    a.href = result.url;
    a.download = `${image.file.name.replace(/\.[^.]+$/, '')}.${result.ext}`;
    a.click();
  };

  return (
    <div className="space-y-5">
      {!image ? (
        <div
          className={`upload-zone rounded-2xl p-10 text-center cursor-pointer ${isDragging ? 'drag-over' : ''}`}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={(e) => { e.preventDefault(); setIsDragging(false); const f = e.dataTransfer.files?.[0]; if (f) loadImage(f); }}
        >
          <div className="w-12 h-12 rounded-2xl bg-blue-50 flex items-center justify-center mx-auto mb-3">
            <span className="text-2xl">🔄</span>
          </div>
          <p className="font-semibold text-text-primary mb-1">Upload Image</p>
          <p className="text-text-muted text-sm">JPG, PNG, or WebP</p>
          <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) loadImage(f); }} />
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center gap-3 bg-surface rounded-xl px-4 py-3 border border-gray-100">
            <div className="w-10 h-10 rounded-xl overflow-hidden bg-gray-50 flex-shrink-0">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src={image.url} alt="Preview" className="w-full h-full object-cover" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-text-primary truncate">{image.file.name}</p>
              <p className="text-xs text-text-muted">{formatBytes(image.size)}</p>
            </div>
            <button onClick={() => { setImage(null); setResult(null); }} className="text-text-muted hover:text-red-500 transition-colors text-xs">Remove</button>
          </div>

          <div>
            <label className="block font-semibold text-text-primary text-sm mb-2">Convert To</label>
            <div className="flex gap-2">
              {FORMATS.map((fmt) => (
                <button
                  key={fmt.value}
                  onClick={() => { setOutputFormat(fmt.value); setResult(null); }}
                  disabled={image.file.type === fmt.value}
                  className={`flex-1 py-3 rounded-xl text-sm font-semibold transition-all ${outputFormat === fmt.value ? 'bg-brand-blue text-white' : 'bg-surface text-text-secondary hover:bg-gray-100 border border-gray-200'} disabled:opacity-40 disabled:cursor-not-allowed`}
                >
                  {fmt.label}
                </button>
              ))}
            </div>
          </div>

          <button onClick={handleConvert} disabled={processing || image.file.type === outputFormat} className="btn-primary w-full text-base py-3.5 disabled:opacity-50 disabled:cursor-not-allowed">
            {processing ? 'Converting…' : 'Convert Image'}
          </button>

          {result && (
            <div className="bg-green-50 border border-green-100 rounded-2xl p-4">
              <div className="flex items-center justify-between mb-3">
                <p className="font-semibold text-green-800">Conversion complete!</p>
                <p className="text-sm text-green-700">{formatBytes(result.size)}</p>
              </div>
              <button onClick={handleDownload} className="w-full py-3 rounded-xl bg-green-600 hover:bg-green-700 text-white font-semibold text-sm transition-colors flex items-center justify-center gap-2">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                </svg>
                Download {FORMATS.find((f) => f.value === outputFormat)?.label}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
